// src/services/admin.productService.js
import { apiClient } from "../../config/api.config";

class ProductService {
  /**
   * Get all products with pagination and filtering
   * @param {object} params - Parameters for filtering and pagination
   * @returns {Promise} Promise with the response data
   */
  async getAllProducts(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page || 1,
        limit: params.limit || 10,
        ...(params.sector && { sector: params.sector }),
        ...(params.industry && { industry: params.industry }),
        ...(params.searchTerm && { searchTerm: params.searchTerm }), // Changed from 'search' to 'searchTerm'
        ...(params.status && { status: params.status }), // Add status filter if needed
      }).toString();

      const response = await apiClient.get(
        `/products/user/allproducts?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get product history for a specific product by uniqueTitle
   * @param {string} uniqueTitle - Unique title of the product
   * @returns {Promise} Promise with the response data
   */
  async getProductHistory(uniqueTitle) {
    try {
      const response = await apiClient.get(`/products/history/${uniqueTitle}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update a product by uniqueTitle
   * @param {string} uniqueTitle
   * @param {object} data
   * @returns {Promise}
   */
  async updateProduct(uniqueTitle, data) {
    try {
      const response = await apiClient.put(
        `/admin/product/${uniqueTitle}`,
        data
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Export products data as CSV
   * @returns {Promise} Promise with the blob data for download
   */
  async exportProductsCSV() {
    try {
      const response = await apiClient.get(`/products/export-xlsx`, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Import products data from a CSV file
   * @param {File} file
   * @param {boolean} updateExisting
   * @returns {Promise}
   */
  async importProductsCSV(file, updateExisting = true) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("updateExisting", updateExisting.toString());

      const response = await apiClient.post(`/products/import-xlsx`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get detailed information for a specific product
   * @param {string} productId
   * @returns {Promise}
   */
  async getProductDetail(productId) {
    try {
      const response = await apiClient.get(`/products/${productId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a new product
   * @param {object} productData
   * @returns {Promise}
   */
  // async createProduct(productData) {
  //   try {
  //     const response = await apiClient.post(`/products`, productData);
  //     return response.data;
  //   } catch (error) {
  //     throw error;
  //   }
  // }

  /**
   * Update an existing product by ID
   * @param {string} productId
   * @param {object} productData
   * @returns {Promise}
   */
  async updateProductById(productId, productData) {
    try {
      const response = await apiClient.put(
        `/products/${productId}`,
        productData
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete a product by ID
   * @param {string} productId
   * @returns {Promise}
   */
  async deleteProduct(productId) {
    try {
      const response = await apiClient.delete(
        `/products/admin/product/id/${productId}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get all product sectors (for filtering)
   * @returns {Promise}
   */
  async getProductSectors() {
    try {
      const response = await apiClient.get(`/products/sectors`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all product industries (for filtering)
   * @returns {Promise}
   */
  async getProductIndustries() {
    try {
      const response = await apiClient.get(`/products/industries`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get price history for a specific product
   * @param {string} productId - Product ID
   * @param {string} snapshotType - Type of snapshot ('1w', '1m', '1y', '10y')
   * @param {number} limit - Number of records to fetch (default: 100)
   * @returns {Promise} Promise with the price history data
   */
  async getPriceHistory(productId, snapshotType = "1m", limit = 100) {
    try {
      const response = await apiClient.get(
        `/products/price-history/${productId}/${snapshotType}/${limit}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create manual price snapshot for a product
   * @param {string} productId - Product ID
   * @returns {Promise} Promise with the response data
   */
  async createManualPriceSnapshot(productId) {
    try {
      const response = await apiClient.post(
        `/products/price-history/snapshot/${productId}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Cleanup old price snapshots
   * @returns {Promise} Promise with the response data
   */
  async cleanupOldPriceSnapshots() {
    try {
      const response = await apiClient.delete(
        `/products/price-history/cleanup`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get price history chart data for multiple time periods
   * @param {string} productId - Product ID
   * @param {Array} snapshotTypes - Array of snapshot types ['1w', '1m', '1y', '10y']
   * @returns {Promise} Promise with chart data for all requested periods
   */
  async getPriceHistoryChart(productId, snapshotTypes = ["1m"]) {
    try {
      const promises = snapshotTypes.map((type) =>
        this.getPriceHistory(productId, type, 50)
      );

      const results = await Promise.all(promises);

      const chartData = {};
      snapshotTypes.forEach((type, index) => {
        chartData[type] = results[index];
      });

      return chartData;
    } catch (error) {
      throw error;
    }
  }

  // ====================== NEW API FUNCTIONS FOR PRODUCT SERVICE ======================

  /**
   * Get comprehensive stock analytics and technical indicators
   * @param {string} productId - Product ID
   * @param {string} timeRange - Time range for analysis ('1m', '1y', '10y')
   * @returns {Promise} Promise with detailed analytics data
   */
  async getStockAnalytics(productId, timeRange = "1y") {
    try {
      const response = await apiClient.get(
        `/products/price-history/analytics/${productId}?timeRange=${timeRange}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get market overview with top gainers, losers, and sector performance
   * @param {object} params - Parameters for market overview
   * @param {string} params.sector - Filter by specific sector (optional)
   * @param {number} params.limit - Number of stocks to return (default: 20)
   * @param {string} params.sortBy - Sort criteria ('marketCap', 'percentage')
   * @returns {Promise} Promise with market overview data
   */
  async getMarketOverview(options = {}) {
    const params = new URLSearchParams();
    try {
      // Add all the new parameters
      if (options.sector && options.sector !== "all")
        params.append("sector", options.sector);
      if (options.industry && options.industry !== "all")
        params.append("industry", options.industry);
      if (options.limit) params.append("limit", options.limit);
      if (options.sortBy) params.append("sortBy", options.sortBy);
      if (options.sortOrder) params.append("sortOrder", options.sortOrder);
      if (options.minPrice) params.append("minPrice", options.minPrice);
      if (options.maxPrice) params.append("maxPrice", options.maxPrice);
      if (options.trend) params.append("trend", options.trend);
      if (options.priceRange) params.append("priceRange", options.priceRange);
      if (options.includeMetrics)
        params.append("includeMetrics", options.includeMetrics);

      const response = await apiClient.get(
        `/products/price-history/market-overview?${params}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get chart data for multiple stocks for portfolio/comparison view
   * @param {Array} productIds - Array of product IDs
   * @param {string} timeRange - Time range ('1w', '1m', '1y', '10y')
   * @returns {Promise} Promise with multiple stocks chart data
   */
  async getMultipleStocksData(productIds, timeRange = "1m") {
    try {
      const response = await apiClient.post(
        `/products/price-history/multiple?timeRange=${timeRange}`,
        { productIds }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get comprehensive stock chart data for visualization
   * @param {string} productId - Product ID
   * @param {string} timeRange - Time range ('1w', '1m', '1y', '10y')
   * @param {string} chartType - Chart type ('line', 'bar', 'area')
   * @returns {Promise} Promise with stock chart data
   */
  async getStockChartData(productId, timeRange = "1m", chartType = "line") {
    try {
      const queryParams = new URLSearchParams({
        timeRange,
        ...(chartType && { chartType }),
      }).toString();

      const response = await apiClient.get(
        `/products/price-history/chart/${productId}?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Search stocks by name or symbol
   * @param {string} query - Search query
   * @param {number} limit - Number of results (default: 10)
   * @returns {Promise} Promise with search results
   */
  async searchStocks(query, limit = 10) {
    try {
      const queryParams = new URLSearchParams({
        q: query,
        limit: limit.toString(),
      }).toString();

      const response = await apiClient.get(
        `/products/price-history/search?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get trending stocks based on price movement and volume
   * @param {number} limit - Number of trending stocks (default: 10)
   * @param {string} timeframe - Timeframe for trending ('1d', '1w', '1m')
   * @returns {Promise} Promise with trending stocks data
   */
  async getTrendingStocks(limit = 10, timeframe = "1d") {
    try {
      const queryParams = new URLSearchParams({
        limit: limit.toString(),
        timeframe,
      }).toString();

      const response = await apiClient.get(
        `/products/price-history/trending?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get list of all available sectors
   * @returns {Promise} Promise with sectors list
   */
  async getAvailableSectors() {
    try {
      const response = await apiClient.get(`/products/price-history/sectors`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get product performance comparison for multiple products
   * @param {Array} productIds - Array of product IDs to compare
   * @param {string} timeRange - Time range for comparison ('1w', '1m', '1y', '10y')
   * @returns {Promise} Promise with comparison data
   */
  async getProductComparison(productIds, timeRange = "1m") {
    try {
      const multipleStocksData = await this.getMultipleStocksData(
        productIds,
        timeRange
      );

      // Enhanced comparison logic
      const comparisonData = {
        timeRange,
        products: multipleStocksData.data,
        comparison: {
          bestPerformer: null,
          worstPerformer: null,
          mostVolatile: null,
          leastVolatile: null,
          avgPerformance: 0,
        },
      };

      if (multipleStocksData.data.length > 0) {
        const products = multipleStocksData.data;

        // Find best and worst performers
        comparisonData.comparison.bestPerformer = products.reduce(
          (best, current) =>
            current.percentage > best.percentage ? current : best
        );

        comparisonData.comparison.worstPerformer = products.reduce(
          (worst, current) =>
            current.percentage < worst.percentage ? current : worst
        );

        // Calculate average performance
        comparisonData.comparison.avgPerformance =
          products.reduce((sum, product) => sum + product.percentage, 0) /
          products.length;

        // Simple volatility calculation based on price range
        const withVolatility = products.map((product) => ({
          ...product,
          volatility:
            product.high52Week && product.low52Week
              ? ((product.high52Week - product.low52Week) /
                product.currentPrice) *
              100
              : 0,
        }));

        comparisonData.comparison.mostVolatile = withVolatility.reduce(
          (most, current) =>
            current.volatility > most.volatility ? current : most
        );

        comparisonData.comparison.leastVolatile = withVolatility.reduce(
          (least, current) =>
            current.volatility < least.volatility ? current : least
        );
      }

      return { success: true, data: comparisonData };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get watchlist performance summary
   * @param {Array} productIds - Array of product IDs in watchlist
   * @returns {Promise} Promise with watchlist performance data
   */
  async getWatchlistPerformance(productIds) {
    try {
      const multipleStocksData = await this.getMultipleStocksData(
        productIds,
        "1d"
      );

      const performanceData = {
        totalProducts: productIds.length,
        gainers: 0,
        losers: 0,
        unchanged: 0,
        totalGainLoss: 0,
        avgPerformance: 0,
        topGainer: null,
        topLoser: null,
        products: multipleStocksData.data || [],
      };

      if (multipleStocksData.data && multipleStocksData.data.length > 0) {
        const products = multipleStocksData.data;

        products.forEach((product) => {
          if (product.change > 0) performanceData.gainers++;
          else if (product.change < 0) performanceData.losers++;
          else performanceData.unchanged++;

          performanceData.totalGainLoss += product.change;
        });

        performanceData.avgPerformance =
          performanceData.totalGainLoss / products.length;

        performanceData.topGainer = products.reduce(
          (best, current) =>
            current.percentage > (best?.percentage || -Infinity)
              ? current
              : best,
          null
        );

        performanceData.topLoser = products.reduce(
          (worst, current) =>
            current.percentage < (worst?.percentage || Infinity)
              ? current
              : worst,
          null
        );
      }

      return { success: true, data: performanceData };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get product alerts and notifications
   * @param {string} productId - Product ID
   * @param {object} alertConfig - Alert configuration
   * @param {number} alertConfig.priceTarget - Price target for alert
   * @param {string} alertConfig.condition - Alert condition ('above', 'below')
   * @returns {Promise} Promise with alert status
   */
  async checkPriceAlert(productId, alertConfig) {
    try {
      const productDetail = await this.getProductDetail(productId);
      const currentPrice =
        productDetail.data?.weekData52?.sellPrice ||
        productDetail.data?.weekData52?.buyPrice;

      if (!currentPrice) {
        throw new Error("Unable to fetch current price");
      }

      let alertTriggered = false;
      if (
        alertConfig.condition === "above" &&
        currentPrice >= alertConfig.priceTarget
      ) {
        alertTriggered = true;
      } else if (
        alertConfig.condition === "below" &&
        currentPrice <= alertConfig.priceTarget
      ) {
        alertTriggered = true;
      }

      return {
        success: true,
        data: {
          productId,
          currentPrice,
          targetPrice: alertConfig.priceTarget,
          condition: alertConfig.condition,
          alertTriggered,
          timestamp: new Date().toISOString(),
        },
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get product news and updates (if available)
   * @param {string} productId - Product ID
   * @param {number} limit - Number of news items (default: 10)
   * @returns {Promise} Promise with product news
   */
  async getProductNews(productId, limit = 10) {
    try {
      const response = await apiClient.get(
        `/products/${productId}/news?limit=${limit}`
      );
      return response.data;
    } catch (error) {
      // If news endpoint doesn't exist, return empty news
      if (error.response?.status === 404) {
        return {
          success: true,
          data: {
            news: [],
            message: "No news available for this product",
          },
        };
      }
      throw error;
    }
  }

  /**
   * Get advanced price predictions (if ML model is available)
   * @param {string} productId - Product ID
   * @param {number} days - Number of days to predict (default: 7)
   * @returns {Promise} Promise with price predictions
   */
  async getPricePrediction(productId, days = 7) {
    try {
      const response = await apiClient.get(
        `/products/price-prediction/${productId}?days=${days}`
      );
      return response.data;
    } catch (error) {
      // If prediction endpoint doesn't exist, return basic trend analysis
      if (error.response?.status === 404) {
        const history = await this.getPriceHistory(productId, "1m", 30);

        if (history.data.length > 0) {
          const prices = history.data.map(
            (h) => h.priceData.sellPrice || h.priceData.buyPrice
          );
          const trend = this.calculateSimpleTrend(prices);

          return {
            success: true,
            data: {
              predictions: [],
              trend,
              message:
                "Advanced predictions not available. Basic trend analysis provided.",
            },
          };
        }
      }
      throw error;
    }
  }

  /**
   * Helper function to calculate simple trend
   * @private
   */
  calculateSimpleTrend(prices) {
    if (prices.length < 5) return "insufficient_data";

    const recent = prices.slice(0, 5);
    const older = prices.slice(-5);

    const recentAvg =
      recent.reduce((sum, price) => sum + price, 0) / recent.length;
    const olderAvg =
      older.reduce((sum, price) => sum + price, 0) / older.length;

    const change = ((recentAvg - olderAvg) / olderAvg) * 100;

    if (change > 2) return "bullish";
    if (change < -2) return "bearish";
    return "neutral";
  }
  /**
   * Update product availability settings
   * @param {string} productId - The product ID
   * @param {object} data - Availability data
   * @param {number} data.totalUnits - Total units available
   * @param {number} data.lowStockThreshold - Low stock threshold
   * @param {boolean} data.trackingEnabled - Whether tracking is enabled
   * @returns {Promise} Promise with the response data
   */
  async updateAvailability(productId, data) {
    try {
      const response = await apiClient.put(
        `/products/admin/product/${productId}/availability`,
        data
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Reserve product units
   * @param {string} productId - The product ID
   * @param {number} quantity - Quantity to reserve
   * @returns {Promise} Promise with the response data
   */
  async reserveUnits(productId, quantity) {
    try {
      const response = await apiClient.post(
        `/products/admin/product/${productId}/reserve`,
        { quantity }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Sell product units
   * @param {string} productId - The product ID
   * @param {number} quantity - Quantity to sell
   * @returns {Promise} Promise with the response data
   */
  async sellUnits(productId, quantity) {
    try {
      const response = await apiClient.post(
        `/products/admin/product/${productId}/sell`,
        { quantity }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get availability statistics
   * @returns {Promise} Promise with the response data
   */
  async getAvailabilityStats() {
    try {
      const response = await apiClient.get(
        "/products/admin/availability/stats"
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get low stock products with pagination
   * @param {number} page - Page number (default: 1)
   * @param {number} limit - Items per page (default: 10)
   * @param {string} searchTerm - Optional search term to filter products
   * @returns {Promise} Promise with the response data
   */
  async getLowStockProducts(page = 1, limit = 10, searchTerm = "") {
    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(searchTerm && { search: searchTerm }),
      }).toString();

      const response = await apiClient.get(
        `/products/admin/availability/low-stock?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get product availability for public users
   * @param {string} productId - The product ID
   * @returns {Promise} Promise with the response data
   */
  async getProductAvailability(productId) {
    try {
      const response = await apiClient.get(
        `/products/user/product/${productId}/availability`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Search products by availability status
   * @param {object} params - Search parameters
   * @param {string} params.status - Availability status (in-stock, low-stock, out-of-stock)
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.searchTerm - Search term for product name
   * @returns {Promise} Promise with the response data
   */
  async searchProductsByAvailability(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        page: params.page?.toString() || "1",
        limit: params.limit?.toString() || "10",
        ...(params.status && { status: params.status }),
        ...(params.searchTerm && { search: params.searchTerm }),
      }).toString();

      const response = await apiClient.get(
        `/products/admin/availability/low-stock?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Bulk update availability for multiple products
   * @param {Array} updates - Array of update objects
   * @param {string} updates[].productId - Product ID
   * @param {object} updates[].data - Availability data to update
   * @returns {Promise} Promise with the response data
   */
  async bulkUpdateAvailability(updates) {
    try {
      const promises = updates.map(({ productId, data }) =>
        this.updateAvailability(productId, data)
      );
      const results = await Promise.allSettled(promises);

      const successful = results.filter(
        (result) => result.status === "fulfilled"
      );
      const failed = results.filter((result) => result.status === "rejected");

      return {
        success: true,
        data: {
          successful: successful.length,
          failed: failed.length,
          results: results,
        },
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get availability history for a product (if your backend supports it)
   * @param {string} productId - The product ID
   * @param {object} params - Query parameters
   * @param {string} params.startDate - Start date for history
   * @param {string} params.endDate - End date for history
   * @returns {Promise} Promise with the response data
   */
  async getAvailabilityHistory(productId, params = {}) {
    try {
      const queryParams = new URLSearchParams({
        ...(params.startDate && { startDate: params.startDate }),
        ...(params.endDate && { endDate: params.endDate }),
      }).toString();

      const response = await apiClient.get(
        `/products/admin/product/${productId}/availability/history?${queryParams}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Export availability data to CSV
   * @param {object} params - Export parameters
   * @param {string} params.status - Filter by status
   * @param {string} params.format - Export format (csv, xlsx)
   * @returns {Promise} Promise with the response data
   */
  async exportAvailabilityData(params = {}) {
    try {
      const queryParams = new URLSearchParams({
        format: params.format || "csv",
        ...(params.status && { status: params.status }),
      }).toString();

      const response = await apiClient.get(
        `/products/admin/availability/export?${queryParams}`,
        { responseType: "blob" }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a new product
   * @param {object} productData - Product data to create
   * @returns {Promise} Promise with the response data
   */
  async createProduct(productData) {
    try {
      // Validate required fields before sending
      if (!productData.basicData?.legalName || !productData.basicData?.cin) {
        return {
          success: false,
          message: "Legal name and CIN are required fields",
          error: "Validation error",
        };
      }

      const response = await apiClient.post(
        "/products/admin/product", // Make sure this matches your backend route
        productData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      return {
        success: true,
        data: response.data,
        message: response.data.message || "Product created successfully",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to create product. Please check your network connection.",
        error: error.response?.data || error.message,
      };
    }
  }

  /**
   * Update an existing product
   * @param {string} uniqueTitle - Unique title of the product to update
   * @param {object} productData - Updated product data
   * @returns {Promise} Promise with the response data
   */
  async updateProduct(uniqueTitle, productData) {
    try {
      const response = await apiClient.put(
        `/products/admin/product/${uniqueTitle}`,
        productData
      );
      return {
        success: true,
        data: response.data,
        message: "Product updated successfully",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to update product",
        error: error.response?.data,
      };
    }
  }

  /**
   * Delete a product by uniqueTitle (by slug/title)
   * Use this only when you have the uniqueTitle. For deletion by MongoDB _id, use deleteProduct(id).
   * @param {string} uniqueTitle - Unique title of the product
   * @returns {Promise}
   */
  async deleteProductByTitle(uniqueTitle) {
    try {
      const response = await apiClient.delete(
        `/products/admin/product/${uniqueTitle}`
      );
      return {
        success: true,
        data: response.data,
        message: "Product deleted successfully",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to delete product",
        error: error.response?.data,
      };
    }
  }

  /**
   * Get all products with pagination
   * @param {number} page - Page number for pagination
   * @param {object} filters - Optional filters
   * @param {string} filters.status - Filter by availability status
   * @param {string} filters.search - Search term
   * @param {number} filters.limit - Items per page
   * @returns {Promise} Promise with the response data
   */

  /**
   * Bulk update products
   * @param {array} updates - Array of product update objects
   * @param {string} updates[].uniqueTitle - Product unique title
   * @param {object} updates[].data - Product data to update
   * @returns {Promise} Promise with the response data
   */
  async bulkUpdateProducts(updates) {
    try {
      const response = await apiClient.post("/admin/products/bulk-update", {
        updates,
      });
      return {
        success: true,
        data: response.data,
        message: "Products bulk updated successfully",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message || "Failed to bulk update products",
        error: error.response?.data,
      };
    }
  }

  /**
   * Bulk delete products
   * @param {array} uniqueTitles - Array of product unique titles to delete
   * @returns {Promise} Promise with the response data
   */
  async bulkDeleteProducts(uniqueTitles) {
    try {
      const response = await apiClient.post("/admin/products/bulk-delete", {
        uniqueTitles,
      });
      return {
        success: true,
        data: response.data,
        message: "Products bulk deleted successfully",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message || "Failed to bulk delete products",
        error: error.response?.data,
      };
    }
  }

  /**
   * Get product details by unique title
   * @param {string} uniqueTitle - Product unique title
   * @returns {Promise} Promise with the response data
   */
  async getProductByUniqueTitle(uniqueTitle) {
    try {
      const response = await apiClient.get(`/admin/product/${uniqueTitle}`);
      return {
        success: true,
        data: response.data,
        message: "Product fetched successfully",
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to fetch product",
        error: error.response?.data,
      };
    }
  }
  async getAllSellRequests(params = {}) {
    try {
      // Construct query parameters
      const queryParams = new URLSearchParams();

      // Add optional filters
      if (params.status) queryParams.append("status", params.status);
      if (params.sellType) queryParams.append("sellType", params.sellType);
      if (params.search) queryParams.append("search", params.search);

      // Add pagination parameters
      if (params.page) queryParams.append("page", params.page);
      if (params.limit) queryParams.append("limit", params.limit);

      // Add sorting parameters
      if (params.sortBy) queryParams.append("sortBy", params.sortBy);
      if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);

      const response = await apiClient.get(
        `/transactions/admin/sell-requests?${queryParams.toString()}`
      );
      return {
        success: true,
        data: response.data.data,
        pagination: response.data.pagination,
        filters: response.data.filters,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Failed to fetch sell requests",
        data: error.response?.data,
      };
    }
  }
  async updateSellRequestStatus(requestId, payload) {
    try {
      const response = await apiClient.put(
        `/transactions/sell-requests/${requestId}/status`,
        payload
      );

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Request status updated successfully",
      };
    } catch (error) {
      return {
        success: false,
        error:
          error.response?.data?.message || "Failed to update request status",
        data: error.response?.data,
      };
    }
  }

  /**
   * Upload a product logo image file via multipart/form-data.
   * @param {string} uniqueTitle - Product unique title (slug)
   * @param {File} file - The image File object selected by the user
   * @returns {Promise} Promise with { success, logo (data URI) }
   */
  async uploadProductLogo(uniqueTitle, file) {
    try {
      const formData = new FormData();
      formData.append("logo", file);

      const response = await apiClient.post(
        `/products/admin/product/${uniqueTitle}/logo`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return {
        success: true,
        logo: response.data.logo,
        message: response.data.message || "Logo uploaded successfully",
      };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message || "Failed to upload logo",
        error: error.response?.data,
      };
    }
  }
}

export default new ProductService();
